import React, { useEffect, useRef, useState, createContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuthStore } from './store/auth';
import { extendSession, isSessionValid } from './utils/auth';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import ScrollToTop from './components/ScrollToTop';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ToastProvider } from './components/ui/Toast';

// Components
import StoreHeader from './views/base/StoreHeader';
import StoreFooter from './views/base/StoreFooter';
import Home from './views/store/Home';
import Products from './views/store/Products';
import ProductDetail from './views/store/ProductDetail';
import Cart from './views/store/Cart';
import Checkout from './views/store/Checkout';
import PaymentSuccess from './views/store/PaymentSuccess';

import Login from './views/auth/Login';
import Register from './views/auth/Register';
import Logout from './views/auth/Logout';
import ForgotPassword from './views/auth/ForgotPassword';
import CreateNewPassword from './views/auth/CreatePassword';

import Account from './views/customer/Account';
import Orders from './views/customer/Orders';
import OrderDetail from './views/customer/OrderDetail';
import Wishlist from './views/customer/Wishlist';
import CustomerNotification from './views/customer/CustomerNotifications';
import Invoice from './views/customer/Invoice';
import CustomerSettings from './views/customer/Settings';

import Search from './views/store/Search';
import About from './views/store/About';
import Contact from './views/store/Contact';
import Privacy from './views/store/Privacy';
import Terms from './views/store/Terms';
import FAQ from './views/store/FAQ';
import Support from './views/store/Support';
import PorMayor from './views/store/PorMayor';
import Vendedores from './views/store/Vendedores';

import WebsiteLoadingSkeleton from './components/WebsiteLoadingSkeleton';
import './components/MetallicLoadingEffect.css';

import apiInstance from './utils/axios';
import getCartID from './views/plugin/cartID';
import UserData from './views/plugin/UserData';

// Cart context
export const CartContext = createContext();

// Floating WhatsApp button — fetches number from store-info
function FloatingWhatsApp() {
  return (
    <a
      href="/vendedores/"
      className="fixed bottom-6 right-6 z-[9999] w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl hover:scale-110 hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] transition-all duration-300 active:scale-95"
      aria-label="WhatsApp"
    >
      <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-8 md:h-8" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
}

function AppContent() {
  const [cartCount, setCartCount] = useState(0);

  const cartId = getCartID();
  const userData = UserData();
  const prevCartCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const loading = useAuthStore((state) => state.loading);

  // Initialize auth
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Session persistence
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isSessionValid()) {
        await extendSession();
      }
    }, 30 * 60 * 1000);

    const handleActivity = async () => {
      if (isSessionValid()) {
        await extendSession();
      }
    };

    window.addEventListener('focus', handleActivity);
    document.addEventListener('visibilitychange', handleActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleActivity);
      document.removeEventListener('visibilitychange', handleActivity);
    };
  }, []);

  useEffect(() => {
    if (cartId && !isInitialLoadRef.current) {
      fetchCartCount();
    }
  }, [cartId]);

  useEffect(() => {
    if (cartId) {
      fetchCartCount();
      isInitialLoadRef.current = false;
    }
  }, [cartId]);

  const fetchCartCount = async () => {
    try {
      if (!cartId) return;

      const url = userData?.user_id
        ? `cart-list/${cartId}/${userData.user_id}/`
        : `cart-list/${cartId}/`;

      const response = await apiInstance.get(url);
      const count = response.data.cart_items?.length || 0;

      setCartCount(count);

      if (count > prevCartCountRef.current && prevCartCountRef.current > 0) {
        // Toast handled by components now
      }

      prevCartCountRef.current = count;
    } catch {
      setCartCount(0);
    }
  };

  const updateCartCountAndNotify = (newCount) => {
    setCartCount(newCount);
    prevCartCountRef.current = newCount;
  };

  if (loading) {
    return <WebsiteLoadingSkeleton />;
  }

  return (
    <CartContext.Provider value={[cartCount, updateCartCountAndNotify]}>
      <div className="loading-complete">
        <BrowserRouter>
          <ScrollToTop />
          <StoreHeader />

          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products/" element={<Products />} />
            <Route path="/por-mayor/" element={<PorMayor />} />
            <Route path="/detail/:slug/" element={<ProductDetail />} />
            <Route path="/detail/:slug/:color/:size/" element={<ProductDetail />} />
            <Route path="/cart/" element={<Cart />} />
            <Route path="/carts" element={<Navigate to="/cart/" replace />} />
            <Route path="/carts/" element={<Navigate to="/cart/" replace />} />
            <Route path="/checkout/:order_oid/" element={<Checkout />} />
            <Route path="/payment-success/:order_oid/" element={<PaymentSuccess />} />

            <Route path="/login/" element={<Login />} />
            <Route path="/register/" element={<Register />} />
            <Route path="/logout/" element={<Logout />} />
            <Route path="/forgot-password/" element={<ForgotPassword />} />
            <Route path="/create-new-password/" element={<CreateNewPassword />} />

            <Route path="/customer/account/" element={<Account />} />
            <Route path="/customer/orders/" element={<Orders />} />
            <Route path="/customer/orders/:order_oid/" element={<OrderDetail />} />
            <Route path="/customer/wishlist/" element={<Wishlist />} />
            <Route path="/customer/notifications/" element={<CustomerNotification />} />
            <Route path="/customer/invoice/:order_oid/" element={<Invoice />} />
            <Route path="/customer/settings/" element={<CustomerSettings />} />

            <Route path="/search/" element={<Search />} />
            <Route path="/about/" element={<About />} />
            <Route path="/contact/" element={<Contact />} />
            <Route path="/privacy/" element={<Privacy />} />
            <Route path="/terms/" element={<Terms />} />
            <Route path="/faq/" element={<FAQ />} />
            <Route path="/support/" element={<Support />} />
            <Route path="/vendedores/" element={<Vendedores />} />
          </Routes>

          <StoreFooter />
          <Analytics />
          <SpeedInsights />
          <FloatingWhatsApp />
        </BrowserRouter>
      </div>
    </CartContext.Provider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <CurrencyProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
